import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import client, { formatZAR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, Clock } from "lucide-react";

const PAYMENT_LABEL = { payfast: "Card (PayFast)", cash: "Cash on collection", whatsapp: "WhatsApp · pay on collection" };
const MAX_TRIES = 10;

function OrderDetails({ order, confirmed }) {
    return (
        <div data-testid="order-details" className="mt-10 border border-brand-line p-8 rounded-sm text-left bg-brand-charcoal">
            <div className="flex justify-between text-sm">
                <span className="text-brand-mute">Order number</span>
                <span data-testid="order-number" className="text-brand-gold">{order.order_number}</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
                <span className="text-brand-mute">Name</span>
                <span>{order.customer?.name}</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
                <span className="text-brand-mute">Payment</span>
                <span data-testid="order-payment">{order.payment_method === "payfast" ? `${PAYMENT_LABEL.payfast} · ${order.payment_status === "paid" ? "Paid" : "Awaiting confirmation"}` : PAYMENT_LABEL[order.payment_method]}</span>
            </div>
            <ul className="mt-6 border-t border-brand-line pt-4 space-y-2 text-sm">
                {order.lines.map((l, i) => (
                    <li key={i} className="flex justify-between gap-4">
                        <span>
                            {l.quantity} × {l.name}
                            {(l.modifiers || []).length > 0 && <span className="block text-xs text-brand-mute">{l.modifiers.map((m) => m.name).join(", ")}</span>}
                        </span>
                        <span>{formatZAR(l.line_total)}</span>
                    </li>
                ))}
            </ul>
            <div className="flex justify-between mt-4 pt-4 border-t border-brand-line">
                <span>Total</span>
                <span data-testid="order-total" className="text-brand-gold">{formatZAR(order.total)}</span>
            </div>
            <p className="mt-6 text-sm text-brand-cream/80">
                Collect from 33 Heronmere Road, Reuven. Usually ready in about {order.prep_time_minutes || 30} minutes.
                {confirmed && order.customer?.email && <> A confirmation has been emailed to {order.customer.email}.</>}
            </p>
        </div>
    );
}

export function CheckoutSuccess() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const { clear } = useCart();
    const [order, setOrder] = useState(null);
    const [timedOut, setTimedOut] = useState(false);
    const orderId = params.get("order_id");

    useEffect(() => {
        if (!orderId) return;
        let tries = 0;
        let timer;
        const tick = async () => {
            try {
                const { data } = await client.get(`/orders/${orderId}`);
                if (data.payment_method === "payfast" && ["failed", "cancelled"].includes(data.payment_status)) {
                    nav(`/checkout?payment=failed`, { replace: true });
                    return;
                }
                setOrder(data);
                if (data.payment_method !== "payfast" || data.payment_status === "paid") {
                    sessionStorage.removeItem("uk_pending_payfast");
                    sessionStorage.removeItem("uk_checkout_form");
                    clear();
                    return;
                }
                tries += 1;
                if (tries < MAX_TRIES) timer = setTimeout(tick, 3000);
                else setTimedOut(true);
            } catch (_) {}
        };
        tick();
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    const confirmed = order && (order.payment_method !== "payfast" || order.payment_status === "paid");
    const heading = !order ? "Loading your order…" : order.payment_method === "payfast" ? (confirmed ? "Payment Confirmed" : "Confirming Payment") : "Order Placed";

    return (
        <div className="container-wide py-24 max-w-2xl mx-auto text-center" data-testid="checkout-success">
            {confirmed ? <CheckCircle2 size={64} className="text-brand-olive mx-auto" /> : <Clock size={64} className="text-brand-gold mx-auto animate-pulse" />}
            <p className="font-script text-brand-gold text-4xl mt-6">{confirmed ? "Thank you" : "One moment…"}</p>
            <h1 data-testid="checkout-success-heading" className="font-display text-4xl mt-2">{heading}</h1>
            {!confirmed && order && (
                <p className="mt-4 text-brand-mute text-sm">
                    {timedOut
                        ? "PayFast hasn't confirmed your payment yet. If you were charged, we'll still receive your order. Please call us on 068 676 0224 if you're unsure."
                        : "We're waiting for PayFast to confirm your payment. This usually takes a few seconds."}
                </p>
            )}
            {order && <OrderDetails order={order} confirmed={confirmed} />}
            <div className="mt-8 flex gap-3 justify-center">
                <Link to="/" className="btn-outline">Back to home</Link>
                <Link to="/order" className="btn-primary">Order more</Link>
            </div>
        </div>
    );
}

export function CheckoutCancel() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const orderId = params.get("order_id");

    useEffect(() => {
        const done = () => {
            sessionStorage.removeItem("uk_pending_payfast");
            nav("/checkout?payment=cancelled", { replace: true });
        };
        if (!orderId) {
            done();
            return;
        }
        client.post(`/orders/${orderId}/cancel-payment`).finally(done);
    }, [orderId, nav]);

    return (
        <div className="container-wide py-24 text-center text-brand-mute" data-testid="checkout-cancel">
            Returning you to your cart…
        </div>
    );
}
